import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  PLAYLISTS, PLAYLIST, MY_SAVED_PLAYLISTS,
  CREATE_PLAYLIST, ADD_PLAYLIST_VIDEO,
  LIKE_PLAYLIST, SAVE_PLAYLIST, DELETE_PLAYLIST,
} from '../graphql';
import {
  ListVideo, Youtube, Heart, Bookmark, BookmarkCheck, Trash2,
  Plus, X, Search, ExternalLink, PlayCircle, BadgeCheck, Clock, Link2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DIFFICULTIES = [
  { value: 'BEGINNER',     label: 'Beginner',     color: 'bg-green-50 text-green-700' },
  { value: 'INTERMEDIATE', label: 'Intermediate', color: 'bg-amber-50 text-amber-700' },
  { value: 'ADVANCED',     label: 'Advanced',     color: 'bg-red-50 text-red-700' },
];

const SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];

function difficultyInfo(value) {
  return DIFFICULTIES.find(d => d.value === value)
    || { value, label: value ? value[0] + value.slice(1).toLowerCase() : 'Unknown', color: 'bg-gray-50 text-gray-600' };
}

// ── Detail Modal ────────────────────────────────────────────────────────────
function PlaylistDetail({ playlistId, currentUserId, onClose }) {
  const [videoForm, setVideoForm] = useState({ title: '', url: '' });
  const { data, loading, error } = useQuery(PLAYLIST, { variables: { id: playlistId }, fetchPolicy: 'cache-and-network' });
  const [addVideo, { loading: adding }] = useMutation(ADD_PLAYLIST_VIDEO, {
    refetchQueries: ['Playlist', 'Playlists', 'MySavedPlaylists'],
  });

  const pl = data?.playlist;
  const isOwn = pl?.curator?.id === String(currentUserId);
  const videos = pl ? [...pl.videos].sort((a, b) => a.orderNum - b.orderNum) : [];
  const diff = pl ? difficultyInfo(pl.difficulty) : null;

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title.trim() || !videoForm.url.trim()) return;
    try {
      await addVideo({
        variables: {
          playlistId,
          title: videoForm.title.trim(),
          youtubeUrl: videoForm.url.trim(),
          orderNum: videos.length + 1,
        },
      });
      setVideoForm({ title: '', url: '' });
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Youtube size={18} className="text-red-500" />
            <h2 className="font-bold text-gray-900">{pl ? pl.title : 'Playlist'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {loading && !pl && (
          <div className="p-8 space-y-3 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-5/6" />
          </div>
        )}
        {error && !pl && (
          <div className="p-8 text-center">
            <p className="text-red-500 text-sm">Couldn't load this playlist. {error.message}</p>
          </div>
        )}
        {!loading && !error && !pl && (
          <div className="p-8 text-center text-gray-400 text-sm">This playlist no longer exists.</div>
        )}

        {pl && (
          <div className="p-5 space-y-4">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{pl.subject}</span>
              {pl.semester && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">Sem {pl.semester}</span>}
              {diff && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diff.color}`}>{diff.label}</span>}
              {pl.isCurated && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                  <BadgeCheck size={11} /> Curated
                </span>
              )}
              {pl.language && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">{pl.language}</span>}
            </div>

            {pl.description && <p className="text-sm text-gray-600 leading-relaxed">{pl.description}</p>}

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
              <span>By {pl.curator?.name || 'Unknown'}</span>
              {pl.channelName && <span>· Channel: {pl.channelName}</span>}
              {pl.totalDuration && <span className="flex items-center gap-1"><Clock size={11} /> {pl.totalDuration}</span>}
              <span>· {pl.videoCount} video{pl.videoCount !== 1 ? 's' : ''}</span>
            </div>

            {pl.youtubePlaylistUrl && (
              <a href={pl.youtubePlaylistUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline bg-indigo-50 px-3 py-1.5 rounded-xl">
                <ExternalLink size={13} /> Open full playlist on YouTube
              </a>
            )}

            {/* Videos */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Videos</h3>
              {videos.length === 0 ? (
                <p className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-xl">No videos added yet.</p>
              ) : (
                <ol className="space-y-1.5">
                  {videos.map((v, i) => (
                    <li key={v.id}>
                      <a href={v.youtubeUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors group">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <PlayCircle size={16} className="text-red-500 flex-shrink-0" />
                        <span className="text-sm text-gray-800 font-medium flex-1 min-w-0 truncate group-hover:text-indigo-700">{v.title}</span>
                        {v.duration && <span className="text-xs text-gray-400 flex-shrink-0">{v.duration}</span>}
                        <ExternalLink size={13} className="text-gray-300 group-hover:text-indigo-500 flex-shrink-0" />
                      </a>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Add video (owner only) */}
            {isOwn && (
              <form onSubmit={handleAddVideo} className="border-t border-gray-100 pt-4 space-y-2">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-1"><Plus size={13} /> Add a video</p>
                <input className="input w-full text-sm" placeholder="Video title" value={videoForm.title}
                  onChange={e => setVideoForm(f => ({ ...f, title: e.target.value }))} required />
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input className="input w-full text-sm pl-9" type="url" placeholder="https://youtube.com/watch?v=…"
                      value={videoForm.url} onChange={e => setVideoForm(f => ({ ...f, url: e.target.value }))} required />
                  </div>
                  <button type="submit" disabled={adding || !videoForm.title.trim() || !videoForm.url.trim()}
                    className="btn-primary text-sm px-4 disabled:opacity-50">
                    {adding ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create Modal ────────────────────────────────────────────────────────────
function CreateModal({ onClose }) {
  const [form, setForm] = useState({
    title: '', description: '', subject: '', semester: '', difficulty: 'BEGINNER',
    firstVideoTitle: '', firstVideoUrl: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [createPlaylist, { loading: creating }] = useMutation(CREATE_PLAYLIST, {
    refetchQueries: ['Playlists', 'MySavedPlaylists'],
  });
  const [addVideo, { loading: addingVideo }] = useMutation(ADD_PLAYLIST_VIDEO, {
    refetchQueries: ['Playlists', 'MySavedPlaylists'],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await createPlaylist({
        variables: {
          title: form.title.trim(),
          description: form.description.trim() || null,
          subject: form.subject.trim(),
          semester: form.semester ? parseInt(form.semester, 10) : null,
          difficulty: form.difficulty,
        },
      });
      const newId = data?.createPlaylist?.id;
      if (newId && form.firstVideoTitle.trim() && form.firstVideoUrl.trim()) {
        await addVideo({
          variables: {
            playlistId: newId,
            title: form.firstVideoTitle.trim(),
            youtubeUrl: form.firstVideoUrl.trim(),
            orderNum: 1,
          },
        });
      }
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  const busy = creating || addingVideo;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <ListVideo size={18} className="text-indigo-600" />
            <h2 className="font-bold text-gray-900">Create playlist</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
            <input className="input w-full" required value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. DSA Crash Course for Placements" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input w-full resize-none" rows={2} value={form.description}
              onChange={e => set('description', e.target.value)} placeholder="What does this learning path cover?" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label>
              <input className="input w-full" required value={form.subject} onChange={e => set('subject', e.target.value)}
                placeholder="e.g. Data Structures" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Semester</label>
              <select className="input w-full" value={form.semester} onChange={e => set('semester', e.target.value)}>
                <option value="">Any semester</option>
                {SEMESTERS.map(s => <option key={s} value={s}>Semester {s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Difficulty</label>
            <div className="flex gap-2">
              {DIFFICULTIES.map(d => (
                <button key={d.value} type="button" onClick={() => set('difficulty', d.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.difficulty === d.value ? d.color + ' border-current' : 'text-gray-500 border-gray-200 hover:border-gray-400'
                  }`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Youtube size={13} className="text-red-500" /> First video (optional)
            </p>
            <div className="space-y-2">
              <input className="input w-full text-sm" placeholder="Video title" value={form.firstVideoTitle}
                onChange={e => set('firstVideoTitle', e.target.value)} />
              <input className="input w-full text-sm" type="url" placeholder="https://youtube.com/watch?v=…"
                value={form.firstVideoUrl} onChange={e => set('firstVideoUrl', e.target.value)}
                required={!!form.firstVideoTitle.trim()} />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1">
              {busy ? 'Creating…' : 'Create playlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Playlist Card ───────────────────────────────────────────────────────────
function PlaylistCard({ playlist: pl, currentUserId, onOpen, onLike, onSave, onDelete }) {
  const isOwn = pl.curator?.id === String(currentUserId);
  const diff = difficultyInfo(pl.difficulty);

  return (
    <div className="card p-4 hover:shadow-md transition-shadow flex flex-col cursor-pointer group" onClick={() => onOpen(pl.id)}>
      {/* Thumbnail */}
      <div className="w-full h-28 rounded-xl overflow-hidden mb-3 relative flex-none" style={{ background: '#1257a0' }}>
        {pl.thumbnailUrl ? (
          <img src={pl.thumbnailUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Youtube size={32} className="text-white/70" />
          </div>
        )}
        <span className="absolute bottom-1.5 right-1.5 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
          <PlayCircle size={11} /> {pl.videoCount} video{pl.videoCount !== 1 ? 's' : ''}
        </span>
        {pl.isCurated && (
          <span className="absolute top-1.5 left-1.5 bg-white/90 text-blue-700 text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
            <BadgeCheck size={11} /> Curated
          </span>
        )}
      </div>

      {/* Chips */}
      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">{pl.subject}</span>
        {pl.semester && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">Sem {pl.semester}</span>}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diff.color}`}>{diff.label}</span>
      </div>

      <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">{pl.title}</h3>
      {pl.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{pl.description}</p>}

      {pl.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {pl.tags.slice(0, 3).map(t => <span key={t} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">#{t}</span>)}
          {pl.tags.length > 3 && <span className="text-xs text-gray-400">+{pl.tags.length - 3}</span>}
        </div>
      )}

      {/* Curator */}
      <div className="flex items-center gap-1.5 mt-3 mb-2">
        <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold flex-shrink-0">
          {pl.curator?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <span className="text-xs text-gray-500 truncate">{pl.curator?.name || 'Unknown'}</span>
        {pl.totalDuration && (
          <span className="text-xs text-gray-400 ml-auto flex items-center gap-1 flex-none"><Clock size={11} /> {pl.totalDuration}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 border-t border-gray-50 pt-3 mt-auto" onClick={e => e.stopPropagation()}>
        <button onClick={() => onLike(pl.id)}
          className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${pl.hasLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}>
          <Heart size={15} fill={pl.hasLiked ? 'currentColor' : 'none'} /> {pl.likes}
        </button>
        <button onClick={() => onSave(pl.id)} title={pl.hasSaved ? 'Remove from saved' : 'Save playlist'}
          className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${pl.hasSaved ? 'text-indigo-600' : 'text-gray-400 hover:text-indigo-500'}`}>
          {pl.hasSaved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />} {pl.saves}
        </button>
        {isOwn && (
          <button onClick={() => onDelete(pl.id)}
            className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 ml-auto" title="Delete playlist">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function PlaylistsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('all'); // 'all' | 'saved'
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [subject, setSubject] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [openId, setOpenId] = useState(null);

  const allQ = useQuery(PLAYLISTS, {
    variables: {
      search: search || undefined,
      difficulty: difficulty || undefined,
      subject: subject || undefined,
    },
    fetchPolicy: 'cache-and-network',
  });
  const savedQ = useQuery(MY_SAVED_PLAYLISTS, { fetchPolicy: 'cache-and-network' });

  const [like] = useMutation(LIKE_PLAYLIST, { refetchQueries: ['Playlists', 'MySavedPlaylists'] });
  const [save] = useMutation(SAVE_PLAYLIST, { refetchQueries: ['Playlists', 'MySavedPlaylists'] });
  const [del]  = useMutation(DELETE_PLAYLIST, { refetchQueries: ['Playlists', 'MySavedPlaylists'] });

  // Saved tab is fetched via MY_SAVED_PLAYLISTS (no server filters) — apply filters client-side.
  const matchesFilters = (pl) =>
    (!search || `${pl.title} ${pl.description || ''} ${pl.subject}`.toLowerCase().includes(search.toLowerCase())) &&
    (!difficulty || pl.difficulty === difficulty) &&
    (!subject || pl.subject.toLowerCase().includes(subject.toLowerCase()));

  const activeQ = tab === 'saved' ? savedQ : allQ;
  const playlists = tab === 'saved'
    ? (savedQ.data?.mySavedPlaylists || []).filter(matchesFilters)
    : (allQ.data?.playlists || []);
  const loading = activeQ.loading && !activeQ.data;
  const error = activeQ.error;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this playlist? This cannot be undone.')) return;
    try {
      await del({ variables: { playlistId: id } });
      if (openId === id) setOpenId(null);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ListVideo className="text-indigo-600" size={26} /> Curated Playlists
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">YouTube learning paths curated by students, for students</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus size={15} /> Create playlist
        </button>
      </div>

      {/* Filter bar */}
      <div className="card p-3 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          {/* Tabs */}
          <div className="flex gap-1">
            <button onClick={() => setTab('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                tab === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-500 border-gray-200 hover:border-gray-400'
              }`}>
              All
            </button>
            <button onClick={() => setTab('saved')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                tab === 'saved' ? 'bg-indigo-600 text-white border-indigo-600' : 'text-gray-500 border-gray-200 hover:border-indigo-300'
              }`}>
              <Bookmark size={12} /> Saved
            </button>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-48 relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-9 py-2 text-sm w-full" placeholder="Search playlists…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {/* Difficulty */}
          <select className="input text-sm py-2 w-auto" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
            <option value="">All difficulties</option>
            {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>

          {/* Subject */}
          <input className="input text-sm py-2 w-36" placeholder="Subject" value={subject}
            onChange={e => setSubject(e.target.value)} />
        </div>
      </div>

      <p className="text-sm text-gray-400">
        {loading ? 'Loading…' : `${playlists.length} playlist${playlists.length !== 1 ? 's' : ''}${tab === 'saved' ? ' saved' : ''}`}
      </p>

      {/* Content */}
      {error && !playlists.length ? (
        <div className="card p-10 text-center">
          <Youtube size={40} className="text-red-200 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Couldn't load playlists</p>
          <p className="text-gray-400 text-sm mt-1">{error.message}</p>
          <button onClick={() => activeQ.refetch()} className="btn-primary mt-4 text-sm">Try again</button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-28 bg-gray-100 rounded-xl mb-3" />
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : playlists.length === 0 ? (
        <div className="card p-12 text-center">
          <ListVideo size={48} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            {tab === 'saved' ? 'No saved playlists' : 'No playlists found'}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {tab === 'saved'
              ? 'Save playlists you like and they\'ll show up here.'
              : 'Be the first to curate a learning path for your classmates!'}
          </p>
          {tab === 'all' && (
            <button onClick={() => setShowCreate(true)} className="btn-primary mt-4">Create playlist</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map(pl => (
            <PlaylistCard
              key={pl.id}
              playlist={pl}
              currentUserId={user?.id}
              onOpen={setOpenId}
              onLike={id => like({ variables: { playlistId: id } }).catch(err => alert(err.message))}
              onSave={id => save({ variables: { playlistId: id } }).catch(err => alert(err.message))}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}
      {openId && <PlaylistDetail playlistId={openId} currentUserId={user?.id} onClose={() => setOpenId(null)} />}
    </div>
  );
}
